<script setup lang="ts">
import { ExperimentCookieName, ExperimentName, ExperimentValue } from '@core/types'
import { isEmpty } from 'es-toolkit/compat'

definePageMeta({
    layout: false,
    middleware: [
        'bingo-landing-page'
    ],
    landingPage: true,
    heroVideo: true,
})

defineOptions({
    prefetch: (to) => {
        usePageData('bingo')
        useSignupPromotions({ query: to.query, server: true, lazy: false })
    },
})

const { data: pageData } = await usePageData('bingo')

const { env } = useCoreConfig()
const experiment = useExperiment({ cookieName: ExperimentCookieName.LANDING_PAGE, experimentName: ExperimentName.LANDING_PAGE })
const { authOpen } = useLandingPageAuth()
const experimentType = computed(() => (experiment.value || ExperimentValue.A) as ExperimentValue)

const { getHead, getSeoMeta } = useContentSeo(
    pageData.value?.seo || {
        metaTitle: 'Online Bingo | Play UK\'s New Bingo Today',
        metaDescription: 'Happy Tiger™ - your go-to spot for exciting Bingo games. T&Cs apply. Play responsibly, 18+ and have a roaring good time!'
    }
)

useHead(getHead())
useSeoMeta(getSeoMeta())

// NOTE:
// Fallback content is used when no page data is available
const fallbackContent = {
    backgroundColor: '#1181c9',
    videoId: '22b2d6aa3f8cf5456f047d27eb568034',
    trustpilotEnabled: undefined,
    usps: undefined
}
</script>

<template>
    <div :class="[`landing-page landing-page--${experiment}`, { 'landing-page--auth-open': authOpen }]" :data-landing-test="experiment || 'a'">
        <UIApp>
            <LandingPageNavBar />

            <main>
                <LandingPageHeaderContainer
                    :type="experimentType"
                    fallback-path="/bingo-lobby"
                    :content="fallbackContent"
                    background-color="#1181c9"
                />
                <StickyTerms
                    title="Significant Conditions"
                    is-landing-page
                    reset-margin
                    :sticky-config="{
                        container: '.landing-page',
                        minHeight: 48,
                        offset: 50,
                        backgroundColor: '#1181c9'
                    }"
                />
                <LazyVisibleNimbusContentDynamicRenderer
                    v-if="!isEmpty(pageData?.dynamicContent)"
                    :content="pageData?.dynamicContent"
                    :max-width="1024"
                    color="#fff"
                    hydrate-on-visible
                />

                <section class="section terms">
                    <div class="section__content">
                        <StickyTerms
                            title="Bonus terms"
                            is-landing-page
                            force-full-terms
                        />
                    </div>
                </section>
            </main>

            <div class="landing-page__footer">
                <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
            </div>

            <LazyEnvBanner v-if="env !== 'prod'" />
        </UIApp>
    </div>
</template>

<style scoped lang="scss">
$body-bg-dark: #1181c9;
$content-width: 1024px;

.landing-page {
    .theme-provider {
        --background: $body-bg-light;
        color: #fff;
    }
}

.dynamic-content {
    > :deep(div:not(.centered-text)) {
        padding-left: 15px;
        padding-right: 15px;
    }

    > :deep(div:not(.panel__body)) {
        padding-top: 40px;
        padding-bottom: 40px;

        @media (min-width: $screen-lg) {
            padding-top: 50px;
            padding-bottom: 50px;
        }
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }

    :deep(.media-text__text) {
        a {
            color: inherit;
        }
    }
    :deep(.media-text__title) {
        a {
            color: inherit;
        }
    }

    :deep(.faq-section__title) {
        font-size: 38px;
    }

    :deep(.question) {
        background-color: #ffffff1a;
        color: #fff;
    }

    :deep(.question__title),
    :deep(.question__answer) {
        color: #fff;
    }

    :deep(.blog-posts) {
        max-width: 800px;
    }
}

.landing-page {
    --theme-background: var(--background, #{$body-bg});
    --body-bg-dark: #{$body-bg-dark};
    --sticky-terms-color: #fff;
    --login-button-color: #fff;

    background-color: var(--body-bg-dark);

    &__footer {
        padding-top: 50px;
        background-color: var(--theme-background, $body-bg);
    }

    :deep(.header__description) {
        font-size: 14px;
    }

    :deep(.text--featured) {
        color: #ffff00;
        font-size: 120%;
    }
}

main {
    .section {
        &__content {
            max-width: $content-width;
            width: 100%;
            margin: auto;
        }

        &--dark {
            background-color: var(--body-bg-dark);
        }
    }

    a {
        color: inherit;
    }

    .join {
        padding-top: 2rem;
        padding-bottom: 3rem;
        color: $white;
        text-align: center;

        @media (min-width: $screen-md) {
            padding-bottom: 4rem;
        }
        .section__content {
            display: grid;
            grid-template-columns: 1fr;
            justify-items: center;
        }

        &__terms {
            padding-bottom: 3rem;

            @media (min-width: $screen-md) {
                padding-bottom: 4rem;
            }

            :deep(.offer-terms__title) {
                margin-bottom: 3px;
                font-size: 12px;
            }
        }
    }
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
    }
}
</style>
